/**
 * Écran de fin du **mode illimité** (DECISIONS §7.6, A-25).
 *
 * Contrairement à un niveau du mode limité, une run illimitée ne se gagne pas : elle s'arrête par
 * **défaite** (PV du parcours à 0) ou par **abandon** (§7 pt 7). L'écran doit donc afficher trois
 * choses que le mode limité n'a pas :
 * - le **motif** de l'arrêt ;
 * - le nombre de **rounds réussis** (`niveauAtteint` de `POST /runs/:id/end`, où le serveur compte
 *   les rounds déjà versés) ;
 * - la règle de perte : « les éclats du round en cours ne sont pas versés ».
 *
 * Tous les montants viennent de la réponse serveur (`eclatsValides`, `eclatsCredites`) : aucun
 * n'est calculé, et `eclatsCredites` n'est **jamais** affiché sous sa forme brute — l'écran en
 * donne le sens (« versés au solde pendant la partie »).
 */
import { useTranslation } from 'react-i18next';

import { formaterNombre } from '../i18n';
import { Bouton } from './Bouton';

import type { FinDeRun } from '../jeu/reduction';

export type MotifFinIllimite = 'defaite' | 'abandon';

export interface ProprietesEcranFinIllimite {
  readonly fin: FinDeRun;
  readonly onRetourAccueil: () => void;
}

export function EcranFinIllimite({ fin, onRetourAccueil }: ProprietesEcranFinIllimite) {
  const { t, i18n } = useTranslation();
  const motif: MotifFinIllimite = fin.raison === 'abandon' ? 'abandon' : 'defaite';
  const nombre = (valeur: number): string => formaterNombre(valeur, i18n.language);
  const roundsReussis = fin.reponse.niveauAtteint;

  return (
    <section
      className="panneau panneau--fin"
      role="status"
      data-fin-run={motif}
      data-motif={motif}
      data-rounds-reussis={roundsReussis}
      data-eclats-verses={fin.reponse.eclatsCredites}
    >
      <h2 className="panneau__titre">
        {motif === 'abandon'
          ? t('jeu.illimite.fin.titreAbandon')
          : t('jeu.illimite.fin.titreDefaite')}
      </h2>
      <p className="note" data-motif-texte={motif}>
        {motif === 'abandon'
          ? t('jeu.illimite.fin.motifAbandon')
          : t('jeu.illimite.fin.motifDefaite')}
      </p>

      <dl className="hud__grille">
        <div className="hud__case">
          <dt className="hud__etiquette">{t('jeu.illimite.fin.roundsReussis')}</dt>
          <dd className="hud__valeur" data-niveau-atteint={roundsReussis}>
            {nombre(roundsReussis)}
          </dd>
        </div>
        <div className="hud__case">
          <dt className="hud__etiquette">{t('jeu.illimite.fin.verseTotal')}</dt>
          <dd className="hud__valeur" data-eclats-verses-total={fin.reponse.eclatsCredites}>
            {nombre(fin.reponse.eclatsCredites)}
          </dd>
        </div>
      </dl>

      <p className="note note--alerte" data-non-verse="oui">
        {t('jeu.illimite.fin.nonVerse')}
      </p>
      {motif === 'abandon' ? (
        <p className="note" data-abandon-enregistre="oui">
          {t('jeu.illimite.fin.abandonEnregistre')}
        </p>
      ) : null}

      <Bouton variante="principal" onClick={onRetourAccueil}>
        {t('commun.retourAccueil')}
      </Bouton>
    </section>
  );
}
